import React, { useState, useEffect } from 'react';
import { useVerbatim } from '../context/VerbatimContext';
import { extractDomainName } from '../utils/URLUtils';
import {
	base64ToImage,
	getVerbatimText,
	hasVerbatimTranslation,
	renderVerbatimTranslation,
} from '../utils/VerbatimUtils';
import BaseModal from './BaseModal';
import PrimaryButton from '../components/buttons/PrimaryButton';
import IconButton from '../components/buttons/IconButton';
import './VerbatimsModal.css';

const VerbatimsModal = ({ title, verbatims, onClose }) => {
  const [localVerbatims, setLocalVerbatims] = useState(
	Array.isArray(verbatims) ? verbatims : []
  );
  const [closing, setClosing] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [sampleSize, setSampleSize] = useState(25);
  const [promptContentSource, setPromptContentSource] = useState('original');
  const { toggleUsedStatus } = useVerbatim();
  const availableTranslationCount = localVerbatims.filter(hasVerbatimTranslation).length;

  useEffect(() => {
    setLocalVerbatims(Array.isArray(verbatims) ? verbatims : []); // Sync local state with prop
    console.log(verbatims)
  }, [verbatims]);

  useEffect(() => {
	if (availableTranslationCount === 0 && promptContentSource === 'translation') {
		setPromptContentSource('original');
	}
  }, [availableTranslationCount, promptContentSource]);

  const handleToggleUsed = (id) => {
    toggleUsedStatus(id); // Update global context
    setLocalVerbatims((prev) =>
      prev.map((verbatim) =>
        verbatim.id === id
          ? { ...verbatim, used: !verbatim.used }
          : verbatim
      )
    ); // Update local state for immediate UI update
  };

  const handleCopyToClipboard = (verbatim) => {
    let verbatimToCopied = "";

    if (typeof verbatim.verbatim === 'string') {
      verbatimToCopied = `${verbatim.verbatim} (${extractDomainName(verbatim.link)})`;
    } else if (Array.isArray(verbatim.verbatim)) {
      const textParts = verbatim.verbatim
        .filter(item => item.type === 'text')
        .map(item => item.content)
        .join('\n');
      
      verbatimToCopied = `${textParts} (${verbatim.labels["Sources"][0].name})`;
    }

    if (verbatimToCopied.trim() !== '') {
      navigator.clipboard.writeText(verbatimToCopied).then(
        () => console.log(`Copied verbatim ID ${verbatim.id} to clipboard.`),
        (err) => console.error('Failed to copy:', err)
      );
    }
  };

  const handleCopyTranslatedToClipboard = (id, link) => {
    const translatedElement = document.querySelector(
      `div[data-verbatim-id="${id}"]`
    );
    if (translatedElement) {
      const translatedText =
        translatedElement.innerText || translatedElement.textContent;
      // Remove the link part from the text
      const formattedText = translatedText.replace(/\)([\s\S]*?)(\bid\b|\bidentifiant\b)[\s\S]*$/i, ')').trim();

      navigator.clipboard.writeText(formattedText).then(
        () => console.log(`Copied translated verbatim ID ${id} to clipboard.`),
        (err) => console.error('Failed to copy translated text:', err)
      );
    } else {
      console.error('Translated content not found in the DOM.');
    }
  };

  const handleCopy = async () => {
	if (!selectedPrompt || !promptOptions[selectedPrompt]) {
		alert('Please select a prompt before copying.');
		return;
	}

	const eligibleVerbatims = promptContentSource === 'translation'
		? localVerbatims.filter(hasVerbatimTranslation)
		: localVerbatims.filter((verbatim) => getVerbatimText(verbatim) !== '');

	const sampleVerbatims = [...eligibleVerbatims]
		.sort(() => 0.5 - Math.random())
		.slice(0, sampleSize);

	if (sampleVerbatims.length === 0) {
		alert(`No ${promptContentSource} verbatims are available to copy.`);
		return;
	}

	const fullPromptText = promptOptions[selectedPrompt].replace('{title}', title);
	const verbatimLines = sampleVerbatims.map((verbatim) => {
		const verbatimText = promptContentSource === 'translation'
			? verbatim.translation.trim()
			: getVerbatimText(verbatim);

		return `- ${verbatimText}`;
	});
	const textToCopy = `${fullPromptText}\n\n${verbatimLines.join('\n')}`;

	try {
		if (!navigator.clipboard?.writeText) {
			throw new Error('Clipboard API is not available.');
		}

		await navigator.clipboard.writeText(textToCopy);
		alert(`Copied ${sampleVerbatims.length} ${promptContentSource} verbatim(s) to clipboard!`);
	} catch (error) {
		console.error('Failed to copy prompt:', error);
		alert('Unable to copy the prompt. Please check the browser clipboard permission and try again.');
	}
  };

  const promptOptions = {
    'Summarize insights': 'Summarize the main insights focusing on "{title}" found in these verbatims, focusing on key points and patterns relevant to this theme. Keep the summary concise and insightful.',
    'Highlight key themes': 'Identify and summarize the key themes from the verbatims about "{title}", capturing the main ideas and insights in a straightforward manner.',
    'Describe common trends': 'Briefly outline common trends and recurring points about "{title}" found in the verbatims, emphasizing significant patterns and frequent observations.',
    'Outline sentiment': 'Provide an overview of positive and negative sentiments related to "{title}" as expressed in the verbatims, highlighting any polarized opinions.',
    'List issues and suggestions': 'Identify frequently mentioned issues and proposed improvements related to "{title}" from the verbatims, with a focus on actionable insights.',
    'Highlight priorities': 'Summarize the top priorities and concerns shared by users about "{title}", capturing what users value or wish to see improved.',
    'Generate FAQ': 'Create a concise FAQ list based on common questions or issues raised about "{title}" in the verbatims, providing a quick reference guide.',
    'Analyze tone and opinions': 'Analyze the tone of the verbatims on "{title}", noting any polarized or strongly positive/negative opinions, and providing a quick overview of sentiment.',
    'Describe use cases': 'Summarize specific use cases or examples shared by users in the verbatims for "{title}", highlighting practical or unique applications.',
    'Identify pain points': 'List common pain points related to "{title}" expressed in the verbatims, along with any suggested solutions or remedies users have proposed.',
    'Extract key terms': 'Identify frequently used terms and phrases in the verbatims about "{title}" to highlight common language and terminology associated with this theme.'
  };

  // New function to handle copying images to clipboard
  const handleCopyImage = async (base64Image) => {
    if (!base64Image) {
      console.error("Image not fournd.");
      return;
    }

    try {
      // Create a canvas element to draw the image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Crate a new image element and set its source to the base64 image
      const img = new Image();
      img.src = base64Image;
      
      // Wait for the image to load
      await new Promise(resolve => { img.onload = resolve; });
      
      // Determine the canvas size based on the image dimensions
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw the image on the canvas
      ctx.drawImage(img, 0, 0);
      
      // Convert the canvas to a blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve));
      // Copy the blob to the clipboard
      if (navigator.clipboard && navigator.clipboard.write) {
        await navigator.clipboard.write([ new ClipboardItem({ [blob.type]: blob }) ]);
      } else {
        throw new Error("L'API Clipboard n'est pas disponible dans votre navigateur");
      }
    } catch (err) {
      console.error("Erreur lors de la copie de l'image:", err);
    }
  };

  const renderVerbatimContent = (verbatim) => {
    if (typeof verbatim.verbatim === 'string') {
      return (
        <>
          <p>{verbatim.verbatim}</p>
          {renderVerbatimTranslation(verbatim.translation)}
        </>
      );
    }

    if (Array.isArray(verbatim.verbatim)) {
      return <div style={{ display: "flex", flexDirection: "column", gap: "5px", width: "100%" }}>
        {verbatim.verbatim.map((item, index) => {
          if (item.type === 'text' && item.content.trim() !== '') {
            return <p key={index}>{item.content}</p>
          } else if (item.type === 'image' && item.content) {
            return (
              <div key={index} style={{ maxWidth: "100%", marginTop: "5px", textAlign: "left" }} className="verbatim-image-thumbnail">
                <img 
                  style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: "5px" }}
                  src={base64ToImage(item.content)} 
                  alt="Verbatim" 
                  onError={(e) => {
                    console.error("Erreur de chargement de l'image", e);
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                  }}
                />
              </div>
            );
          }
          return null;
        })}
        {renderVerbatimTranslation(verbatim.translation)}
      </div>;
    }

    return null;
  };

  return (
    <BaseModal
      show={true}
      title={`Verbatims found for the label ${title}`}
      onClose={onClose}
    >
      <div className="llm-prompt-generator-container">
      <p className="prompt-generator-description">
          Use this tool to generate AI-friendly prompts based on selected
          presets and a sample of random verbatims displayed below.
        </p>
        <div className="llm-prompt-generator">
          <label>Preset Prompts:</label>
          <select
            value={selectedPrompt}
            onChange={(e) => setSelectedPrompt(e.target.value)}
          >
            <option value="">Select a prompt</option>
            {Object.keys(promptOptions).map((shortName, index) => (
              <option key={index} value={shortName}>
                {shortName}
              </option>
            ))}
          </select>
          <label>Number of verbatims:</label>
          <select
            value={sampleSize}
            onChange={(e) => setSampleSize(Number(e.target.value))}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
          <fieldset className="prompt-content-source">
			<legend>Prompt content:</legend>
			<label className={promptContentSource === 'original' ? 'active' : ''}>
				<input
					type="radio"
					name="prompt-content-source"
					value="original"
					checked={promptContentSource === 'original'}
					onChange={(event) => setPromptContentSource(event.target.value)}
				/>
				Original
			</label>
			<label
				className={`${promptContentSource === 'translation' ? 'active' : ''} ${availableTranslationCount === 0 ? 'disabled' : ''}`.trim()}
			>
				<input
					type="radio"
					name="prompt-content-source"
					value="translation"
					checked={promptContentSource === 'translation'}
					disabled={availableTranslationCount === 0}
					onChange={(event) => setPromptContentSource(event.target.value)}
				/>
				Translation ({availableTranslationCount} available)
			</label>
		  </fieldset>
          <PrimaryButton onClick={handleCopy} disabled={!selectedPrompt}>
            Copy
          </PrimaryButton>
        </div>
      </div>

      <div className="verbatims-list">
        {localVerbatims.length > 0 ? (
          localVerbatims.map((verbatim, index) => (
            <div key={index} className="verbatim-item" data-verbatim-id={verbatim.id}>
              {renderVerbatimContent(verbatim)}
              {verbatim.link && (<a href={verbatim.link} target="_blank" rel="noopener noreferrer">
                {verbatim.labels?.["Sources"]?.[0].name &&`(${verbatim.labels["Sources"][0].name})`}
              </a>)}
              <div className="verbatim-actions">
                <div className="small-id">id: {verbatim.id}</div>
                <IconButton
                  iconClass="fa fa-copy"
                  onClick={() => handleCopyToClipboard(verbatim)}
                  tooltip="Copy Original"
                />
                <IconButton
                  iconClass="fa-regular fa-copy"
                  onClick={() =>
                    handleCopyTranslatedToClipboard(
                      verbatim.id,
                      extractDomainName(verbatim.link)
                    )
                  }
                  tooltip="Copy Displayed"
                />
                {Array.isArray(verbatim.verbatim) && verbatim.verbatim.some(item => item.type === 'image') && (
                    <IconButton
                      iconClass="fa-solid fa-file-image"
                      onClick={() => handleCopyImage(base64ToImage(verbatim.verbatim.find(item => item.type === 'image').content))}
                      tooltip="Copy image"
                    />
                )}
                <IconButton
                  iconClass={`fa fa-paperclip ${verbatim.used ? 'used' : ''}`}
                  onClick={() => handleToggleUsed(verbatim.id)}
                  tooltip={
                    verbatim.used ? 'Unmark as Used' : 'Mark as Used'
                  }
                  className={verbatim.used ? 'used' : ''}
                />
              </div>
            </div>
          ))
        ) : (
          <p>No verbatims available.</p>
        )}
      </div>
    </BaseModal>
  );
};

export default VerbatimsModal;
